import { ReactNode, createContext, useContext } from 'react'
import { z } from 'zod'
import { Form } from '$components'
import { CreateLocationFormControls } from './controls'
import { CreateLocationFormContent } from './content'
import { BusinessHour } from '$api-client/types.gen'

export enum Input {
  Name = 'name',
  Address = 'address',
  AddressLineOne = 'line_one',
  AddressLineTwo = 'line_two',
  AddressCity = 'city',
  AddressPostCode = 'post_code',
  AddressCountry = 'country',
  BusinessHours = 'business_hours',
}

const DEFAULT_HOURS: Omit<BusinessHour, 'id'>[] = [
  { day_of_week: 0, open: '09:00', close: '17:00' },
  { day_of_week: 1, open: '09:00', close: '17:00' },
  { day_of_week: 2, open: '09:00', close: '17:00' },
  { day_of_week: 3, open: '09:00', close: '17:00' },
  { day_of_week: 4, open: '09:00', close: '17:00' },
]

export const createLocationSchema = z.object({
  [Input.Name]: z.string()
    .min(1, { message: 'Name is required.' })
    .max(255, { message: 'Name must be less than 255 characters.' }),
  [Input.Address]: z.object({
    [Input.AddressLineOne]: z.string()
      .min(1, { message: 'Address line 1 is required.' })
      .max(255, { message: 'Address line 1 must be less than 255 characters.' }),
    [Input.AddressLineTwo]: z.string()
      .max(255, { message: 'Address line 2 must be less than 255 characters.' })
      .nullable()
      .optional(),
    [Input.AddressCity]: z.string()
      .min(1, { message: 'City is required.' })
      .max(255, { message: 'City must be less than 255 characters.' }),
    [Input.AddressPostCode]: z.string()
      .min(1, { message: 'Post code is required.' })
      .max(255, { message: 'Post code must be less than 255 characters.' }),
    [Input.AddressCountry]: z.string()
      .min(1, { message: 'Country is required.' })
      .max(255, { message: 'Country must be less than 255 characters.' }),
  }),
  [Input.BusinessHours]: z.array(
    z.object({
      day_of_week: z.number().min(0).max(6),
      open: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, { message: 'Invalid time format. Expected HH:MM.' }),
      close: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, { message: 'Invalid time format. Expected HH:MM.' }),
    }),
  ),
})

export type CreateLocationSchema = z.infer<typeof createLocationSchema>

type CreateLocationFormContextType = {
  onSubmit: (values: CreateLocationSchema) => void
  onCancel: () => void
  isLoading?: boolean
}

const CreateLocationFormContext = createContext<CreateLocationFormContextType | undefined>(undefined)

type CreateLocationFormProps = CreateLocationFormContextType & {
  children?: ReactNode
}

export function CreateLocationForm({
  onSubmit,
  onCancel,
  isLoading = false,
  children,
}: CreateLocationFormProps) {
  const context: CreateLocationFormContextType = {
    onSubmit,
    onCancel,
    isLoading,
  }

  return (
    <CreateLocationFormContext.Provider value={context}>
      <Form<CreateLocationSchema, typeof createLocationSchema>
        schema={createLocationSchema}
        onSubmit={onSubmit}
        defaultValues={{
          [Input.BusinessHours]: DEFAULT_HOURS,
        }}
      >
        {children}
      </Form>
    </CreateLocationFormContext.Provider>
  )
}

CreateLocationForm.Content = CreateLocationFormContent
CreateLocationForm.Controls = CreateLocationFormControls

export function useCreateLocationForm() {
  const context = useContext(CreateLocationFormContext)

  if (context === undefined) {
    throw new Error('useCreateLocationForm must be used within a CreateLocationFormProvider')
  }

  return context
}
